import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart, memberSavings, isPalletItem } from "@/context/CartContext";
import { fmtMoney } from "@/lib/api";
import { BadgeCheck, Sparkles, ArrowRight } from "lucide-react";

/**
 * Walmart+/Sam's Club-style member savings ribbon shown at the very top of the
 * homepage. Has two states:
 *   • GUEST  → "Sign up free, save 10% on every order (except pallets)"
 *   • MEMBER → "Welcome back · Your 10% Member Discount is active" with a live
 *              "You're saving $X today" snippet computed from the current cart.
 */
export default function MemberBanner() {
  const { user } = useAuth();
  const { items, subtotal } = useCart();

  // Member state ---------------------------------------------------------------
  if (user) {
    const todaySavings = memberSavings(items, true);
    const eligibleCount = items.filter((i) => !isPalletItem(i)).length;
    const firstName = (user.name || user.email || "there").split(/[\s@]/)[0];
    return (
      <div
        className="bg-[#FFC220] text-[#0A1C40] border-b-2 border-[#0A1C40]"
        data-testid="member-banner-active"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2.5 font-bold">
            <BadgeCheck className="w-5 h-5" strokeWidth={2.5} />
            <span>
              Welcome back, {firstName} —{" "}
              <span className="underline decoration-2 underline-offset-2">
                10% Member Discount
              </span>{" "}
              is active on every order.
            </span>
          </div>
          {todaySavings > 0 ? (
            <div
              className="font-display font-black inline-flex items-center gap-2"
              data-testid="member-banner-savings-amount"
            >
              <Sparkles className="w-4 h-4" />
              You&apos;re saving {fmtMoney(todaySavings)} today
              <span className="text-xs font-bold opacity-80">
                ({eligibleCount} eligible item{eligibleCount === 1 ? "" : "s"} in cart)
              </span>
            </div>
          ) : subtotal > 0 ? (
            <div className="text-xs font-semibold opacity-80">
              Pallet items are excluded from member discount.
            </div>
          ) : (
            <Link
              to="/shop"
              className="font-bold inline-flex items-center gap-1 underline underline-offset-2"
              data-testid="member-banner-shop-link"
            >
              Start saving — Shop now <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Guest state ---------------------------------------------------------------
  return (
    <div
      className="bg-[#0A1C40] text-white border-b-2 border-[#FFC220]"
      data-testid="member-banner-guest"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2.5">
          <Sparkles className="w-4 h-4 text-[#FFC220]" strokeWidth={2.5} />
          <span>
            <span className="text-[#FFC220] font-bold">Free membership</span> —
            save <span className="text-[#FFC220] font-bold">10%</span> on every
            order (except pallets).
          </span>
        </div>
        <Link
          to="/register"
          className="font-bold inline-flex items-center gap-1 bg-[#FFC220] text-[#0A1C40] px-3 py-1 rounded-full hover:bg-white transition-colors"
          data-testid="member-banner-register-cta"
        >
          Sign up free <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
